import React, { useState, useEffect, useRef } from 'react';
import { Logger } from 'stores/Classes';
import {
  Form,
  Input,
  Button,
  Card,
  Row,
  Col,
  Checkbox,
  Typography,
  Divider,
  Space,
  Popconfirm,
  message,
  Modal,
  notification,
} from 'antd';
import {
  DeleteOutlined,
  CopyOutlined,
  EditOutlined,
  PlusOutlined,
  SaveOutlined,
  ConsoleSqlOutlined,
} from '@ant-design/icons';
import { useHistory, useParams } from 'react-router-dom';
import _ from 'lodash';
import styled from 'styled-components';
const { Title, Text, Link } = Typography;

import { ShopStore, CurrentUserStore, InspectionsStore } from 'stores';
import FleetDetails from './common/FleetDetails';
import FormDetails from './common/FormDetails';
import FormGroup from './common/FormGroup';
import { generateRandomId, extractIssuesSorted } from './common/utils';
import { checkBoxPlaceholders } from './common/FormSection';

const ResponsiveEditField = styled.div`
  width: 100%;
  margin: 0 auto;
  padding: 0 24px;
  .edit-template-inner {
    max-width: 1000px;
    margin: 0 auto;
    background: #f5f6fa;
    min-height: 100vh;
    padding: 32px;
    transition:
      max-width 0.2s,
      padding 0.2s;
  }
  @media (max-width: 800px) {
    padding: 0;
    .edit-template-inner {
      max-width: 90vw;
      padding: 8px;
    }
    .ant-row {
      flex-direction: column;
    }
    .ant-col {
      width: 100%;
      max-width: 100%;
    }
    .checkbox-row,
    .checkbox-row.ant-row {
      flex-direction: row !important;
      flex-wrap: nowrap !important;
      overflow-x: auto !important;
    }
    .checkbox-row .ant-col,
    .checkbox-row.ant-row .ant-col {
      width: 25% !important;
      max-width: 25% !important;
    }
    .checkbox-label-text {
      display: none !important;
    }
  }
`;

const ButtonRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  @media (max-width: 600px) {
    flex-wrap: wrap;
    .save-wo-btn {
      flex-basis: 100%;
      margin-top: 8px;
      margin-left: 0;
    }
  }
`;

const CreateForm = () => {
  const [formTitle, setFormTitle] = useState('');
  let { templateId } = useParams();
  let history = useHistory();
  const unblockRef = useRef(null);
  const [isDirty, setIsDirty] = useState(false);

  const [form] = Form.useForm();

  const [fleetAddress, setFleetAddress] = useState('');
  const [fleetLogoURL, setFleetLogoURL] = useState('');

  const [groups, setGroups] = useState([]);
  useEffect(() => {
    const initialize = async () => {
      await loadData(ShopStore.currentShop.id);
    };

    initialize();
  }, []);

  useEffect(() => {
    if (!isDirty) return;

    unblockRef.current = history.block((location, action) => {
      Modal.confirm({
        title: 'Save Changes',
        content: 'Do you want to save changes before exiting?',
        okText: 'Save & Exit',
        cancelText: 'Exit Without Saving',
        onOk: async () => {
          unblockRef.current?.();
          await handleSave(false);
          history.push(location.pathname);
        },
        onCancel: () => {
          unblockRef.current?.();
          history.push(location.pathname);
        },
        okCancel: true,
        closable: true,
        maskClosable: true,
        cancelButtonProps: { danger: true },
      });

      return false;
    });

    return () => {
      unblockRef.current?.();
    };
  }, [isDirty, history]);

  const loadData = async (shopId) => {
    try {
      const shop = await ShopStore.getShopById(shopId);

      const address = _.get(shop, 'address', '');
      const logoUrl = _.get(shop, 'logoUrl', '');

      setFleetAddress(address);
      setFleetLogoURL(logoUrl);

      const template = await InspectionsStore.getTemplate(shopId, templateId);
      setFormTitle(template.title);
      setGroups(template.groups);
    } catch (e) {
      Logger.log(e);
    }
  };

  const handleSave = async () => {
    try {
      const shop_id = ShopStore.currentShop.id;
      const allValues = await form.validateFields();

      const mergedValues = JSON.parse(JSON.stringify(allValues));

      if (mergedValues.groups && groups) {
        mergedValues.groups.forEach((group, gIdx) => {
          const sourceGroup = groups[gIdx] || {};
          group.id = sourceGroup.id;
          group.title = sourceGroup.title;

          if (!group.sections) return;
          group.sections.forEach((section, sIdx) => {
            const sourceSection = sourceGroup.sections?.[sIdx] || {};
            section.checkboxImportant = sourceSection.checkboxImportant || [];
            section.checkboxLabels =
              sourceSection.checkboxLabels || checkBoxPlaceholders;
            section.description = sourceSection.description || '';
            section.freeTextLabel = sourceSection.freeTextLabel || '';

            if (section.images && Array.isArray(section.images)) {
              const uploadedUrls = [];

              for (const img of section.images) {
                if (img.url) {
                  uploadedUrls.push(img.url);
                }
              }

              section.images = uploadedUrls;
            }
          });
        });
      }

      const payload = {
        ...mergedValues,
        form_id: generateRandomId(),
        status: 'new',
        title: formTitle,
        created_by: CurrentUserStore.user.id,
      };

      setIsDirty(false);

      const newForm = await InspectionsStore.createInspectionForm(
        shop_id,
        payload
      );

      history.goBack();

      notification.success({
        message: 'Success',
        description: 'Form created successfully',
      });

      groups.forEach((group, gIdx) => {
        group.sections.forEach((section, sIdx) => {
          const importantFlags = section.checkboxImportant || [];
          const formSection = allValues.groups?.[gIdx]?.sections?.[sIdx];
          if (!formSection) return;
          (formSection.checkboxes || []).forEach((checked, idx) => {
            if (checked && importantFlags[idx]) {
              const label =
                formSection.checkboxLabels?.[idx] ||
                section.checkboxLabels?.[idx] ||
                `Checkbox ${idx + 1}`;
              const freeText = formSection.freeText || '';
            }
          });
        });
      });
    } catch (e) {
      Logger.log(e);
    }
  };

  const handleSaveWithWO = async () => {
    const shop_id = ShopStore.currentShop.id;
    const allValues = await form.validateFields();

    setIsDirty(false);

    const mergedValues = JSON.parse(JSON.stringify(allValues));

    if (mergedValues.groups && groups) {
      mergedValues.groups.forEach((group, gIdx) => {
        const sourceGroup = groups[gIdx] || {};
        group.id = sourceGroup.id;
        group.title = sourceGroup.title;

        if (!group.sections) return;
        group.sections.forEach((section, sIdx) => {
          const sourceSection = sourceGroup.sections?.[sIdx] || {};
          section.checkboxImportant = sourceSection.checkboxImportant || [];
          section.checkboxLabels =
            sourceSection.checkboxLabels || checkBoxPlaceholders;
          section.description = sourceSection.description || '';
          section.freeTextLabel = sourceSection.freeTextLabel || '';

          if (section.images && Array.isArray(section.images)) {
            const uploadedUrls = [];

            for (const img of section.images) {
              if (img.url) {
                uploadedUrls.push(img.url);
              }
            }

            section.images = uploadedUrls;
          }
        });
      });
    }

    const payload = {
      ...mergedValues,
      form_id: generateRandomId(),
      status: 'new',
      title: formTitle,
      created_by: CurrentUserStore.user.id,
    };

    const newForm = await InspectionsStore.createInspectionForm(
      shop_id,
      payload
    );

    const issues = extractIssuesSorted(newForm.groups);

    notification.success({
      message: 'Success',
      description: 'Form created successfully',
    });

    const formData = {
      asset: newForm.asset_id,
      assetType: newForm.asset_type,
      createdBy: newForm.created_by,
      vin: newForm.vin,
      formId: newForm.form_id,
      formTitle: newForm.title,
      issues,
    };

    history.push('/work-order/add', {
      data: formData,
    });
  };

  return (
    <ResponsiveEditField>
      <div className="edit-template-inner">
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}
        >
          <Col>
            <Title level={3}>{formTitle}</Title>
          </Col>
          <Col>
            <ButtonRow>
              <Button
                type="default"
                style={{
                  background: 'rgba(255, 77, 79, 0.2)',
                  borderColor: 'rgba(255, 77, 79, 0.0)',
                }}
                danger
                onClick={() => history.goBack()}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
              >
                Save & Exit
              </Button>
              <Button type="primary" onClick={handleSaveWithWO}>
                Save & Create Work Order
              </Button>
            </ButtonRow>
          </Col>
        </Row>

        <FleetDetails fleetLogoURL={fleetLogoURL} fleetAddress={fleetAddress} />
        <Form
          layout="vertical"
          form={form}
          onValuesChange={() => setIsDirty(true)}
        >
          <FormDetails form={form} />
          {groups.map((group, idx) => (
            <FormGroup
              key={group.id}
              group={group}
              groupIndex={idx}
              form={form}
            />
          ))}
        </Form>
        <Row
          justify="space-between"
          align="middle"
          style={{ marginBottom: 24 }}
        >
          <Col></Col>
          <Col>
            <ButtonRow>
              <Button
                type="default"
                style={{
                  background: 'rgba(255, 77, 79, 0.2)',
                  borderColor: 'rgba(255, 77, 79, 0.0)',
                }}
                danger
                onClick={() => history.goBack()}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
              >
                Save & Exit
              </Button>
              <Button type="primary" onClick={handleSaveWithWO}>
                Save & Create Work Order
              </Button>
            </ButtonRow>
          </Col>
        </Row>
      </div>
    </ResponsiveEditField>
  );
};

export default CreateForm;
